import { useResponse } from 'contexts/response';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { ActiveUnit, Redelivery } from 'types';
import GateUnit from 'types/gate-unit';

const useRedeliveryApi = () => {
  const navigate = useNavigate();
  const { handleError, setSuccess } = useResponse();

  const isRedelivery = useCallback((json?: unknown): json is Redelivery => {
    return !!json && typeof json === 'object' && 'redeliveryNumber' in json;
  }, []);

  const getRedelivery = useCallback(
    async (id: number): Promise<Partial<Redelivery>> => {
      try {
        const response = await fetch(new URL(`/api/redeliveries/${id}`, window.location.origin), {
          method: 'GET',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const json = await response.json();
          if (isRedelivery(json)) return json;
          else handleError(response, json);
        } else handleError(response);
      } catch (e) {
        handleError(e);
      }
      return {};
    },
    [handleError, isRedelivery]
  );

  const findRedelivery = useCallback(
    async (redeliveryNumber: string, customerId: number, locationId: number): Promise<Redelivery | null> => {
      const url = new URL(`/api/redeliveries`, window.location.origin);
      url.searchParams.append('customerId', `${customerId}`);
      url.searchParams.append('locationId', `${locationId}`);
      url.searchParams.append('redeliveryNumber', encodeURIComponent(redeliveryNumber));
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const json = await response.json();
          if (isRedelivery(json)) return json;
        }
      } catch (e) {
        console.error(e);
      }
      return null;
    },
    [isRedelivery]
  );

  const findRedeliveryByUnitId = useCallback(
    async (unitId: number): Promise<Redelivery | null> => {
      try {
        const response = await fetch(new URL(`/api/redeliveries?unitId=${unitId}`, window.location.origin), {
          method: 'GET',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const json = await response.json();
          if (isRedelivery(json)) return json;
        }
      } catch (e) {
        console.error(e);
      }
      return null;
    },
    [isRedelivery]
  );

  const postRedelivery = useCallback(
    async (redelivery: Redelivery) => {
      try {
        const response = await fetch(new URL(`/api/redeliveries`, window.location.origin), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(redelivery)
        });
        if (response.ok) {
          const json = await response.json();
          if (isRedelivery(json)) {
            setSuccess(`Redelivery #${json.redeliveryNumber} created!`);
            navigate(`/redeliveries/${json.id}?location=${json.location.id}`, { replace: true });
          } else handleError(response, json);
        } else handleError(response);
      } catch (e) {
        handleError(e);
      }
    },
    [handleError, isRedelivery, navigate, setSuccess]
  );

  const putRedelivery = useCallback(
    async (id: number, redelivery: Redelivery) => {
      try {
        const response = await fetch(new URL(`/api/redeliveries/${id}`, window.location.origin), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(redelivery)
        });
        if (response.ok) {
          const json = await response.json();
          if (isRedelivery(json)) {
            setSuccess(`Redelivery #${json.redeliveryNumber} updated!`);
            navigate(`/redeliveries/${json.id}?location=${json.location.id}`, { replace: true });
          } else handleError(response, json);
        } else handleError(response);
      } catch (e) {
        handleError(e);
      }
    },
    [handleError, isRedelivery, navigate, setSuccess]
  );

  const reserveUnit = useCallback(
    async (redeliveryId: number, unit: Partial<GateUnit>): Promise<Redelivery | null> => {
      try {
        const response = await fetch(new URL(`/api/redeliveries/${redeliveryId}/units`, window.location.origin), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(unit)
        });
        if (response.ok) {
          const json = await response.json();
          if (isRedelivery(json)) {
            setSuccess(`Unit #${unit.unitNumber} reserved!`);
            return json;
          } else handleError(response, json);
        } else handleError(response);
      } catch (e) {
        handleError(e);
      }
      return null;
    },
    [handleError, isRedelivery, setSuccess]
  );

  const unreserveUnit = useCallback(
    async (redeliveryId: number, unit: ActiveUnit): Promise<Redelivery | null> => {
      try {
        const response = await fetch(
          new URL(`/api/redeliveries/${redeliveryId}/units/${unit.id}`, window.location.origin),
          { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (response.ok) {
          const json = await response.json();
          if (isRedelivery(json)) {
            setSuccess(`Unit #${unit.unitNumber} removed from redelivery.`);
            return json;
          } else handleError(response, json);
        } else handleError(response);
      } catch (e) {
        handleError(e);
      }
      return null;
    },
    [handleError, isRedelivery, setSuccess]
  );

  return {
    findRedelivery,
    findRedeliveryByUnitId,
    getRedelivery,
    postRedelivery,
    putRedelivery,
    reserveUnit,
    unreserveUnit
  };
};

export default useRedeliveryApi;
